import { z } from 'zod';

export const announcementStatusSchema = z.enum([
  'draft',
  'approved',
  'published',
  'withdrawn',
  'archived',
]);
export type AnnouncementStatus = z.infer<typeof announcementStatusSchema>;

export const announcementAudienceSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('all') }),
  z.object({ scope: z.literal('tenants'), tenantIds: z.array(z.string().uuid()).min(1).max(250) }),
]);
export type AnnouncementAudience = z.infer<typeof announcementAudienceSchema>;

export const announcementSchema = z.object({
  id: z.string().min(1),
  title: z.string().trim().min(1).max(120),
  message: z.string().trim().min(1).max(2_000),
  audience: announcementAudienceSchema,
  status: announcementStatusSchema,
  revision: z.number().int().positive(),
  acknowledgementRequired: z.boolean().default(false),
  startsAt: z.string().datetime().optional(),
  expiresAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  createdBy: z.string().min(1),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});
export type Announcement = z.infer<typeof announcementSchema>;

export const announcementDisplaySchema = announcementSchema
  .omit({
    audience: true,
    createdBy: true,
    updatedBy: true,
  })
  .extend({ acknowledgedAt: z.string().datetime().optional() });
export type AnnouncementDisplay = z.infer<typeof announcementDisplaySchema>;

export const announcementAcknowledgementSchema = z.object({
  announcementId: z.string().min(1),
  userId: z.string().min(1),
  scope: z.enum(['global', 'tenant']),
  tenantId: z.string().uuid().optional(),
  acknowledgedAt: z.string().datetime(),
});
export type AnnouncementAcknowledgement = z.infer<typeof announcementAcknowledgementSchema>;

const announcementMutationFieldsSchema = z.object({
  title: announcementSchema.shape.title,
  message: announcementSchema.shape.message,
  audience: announcementSchema.shape.audience,
  status: announcementSchema.shape.status,
  acknowledgementRequired: announcementSchema.shape.acknowledgementRequired,
  selfApprovalConfirmed: z.boolean().default(false),
  startsAt: announcementSchema.shape.startsAt.unwrap().nullable().optional(),
  expiresAt: announcementSchema.shape.expiresAt.unwrap().nullable().optional(),
});

function validateAnnouncementWindow(
  value: {
    startsAt?: string | null | undefined;
    expiresAt?: string | null | undefined;
  },
  context: z.RefinementCtx,
) {
  if (
    value.startsAt &&
    value.expiresAt &&
    new Date(value.expiresAt).getTime() <= new Date(value.startsAt).getTime()
  ) {
    context.addIssue({
      code: 'custom',
      path: ['expiresAt'],
      message: 'Expiry must be later than the start time',
    });
  }
}

export const createAnnouncementSchema = announcementMutationFieldsSchema.superRefine(
  validateAnnouncementWindow,
);

export const updateAnnouncementSchema = announcementMutationFieldsSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated')
  .superRefine(validateAnnouncementWindow);
